import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchRenders, RenderListItem } from '../../api/rendersApi';
import { toApiError, isAbortError } from '../../api/http';
import { EmptyState, StateMessage } from '../../components/ui';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { useApp } from '../../context/AppContext';
import { formatDurationLabel } from '../../utils/duration';
import StoryArtwork from '../../components/StoryArtwork/StoryArtwork';
import { groupTonightRenders, TonightGroup } from './renderPresentation';
import './Renders.css';

/* Group titles follow the slice 2b prompt (confirmed at gate 1); the support
   lines are the Appendix A retained or rewritten strings. */
const GROUP_COPY: Record<TonightGroup, { title: string; description: string; status: string }> = {
  needs_listen: { title: 'Ready for your review', description: 'Listen before your child does, then approve the story you want on the shelf.', status: 'Listen and review' },
  preparing: { title: 'Getting ready', description: 'Your story is on its way.', status: 'Getting ready' },
  needs_help: { title: 'Needs attention', description: 'This story could not be prepared. Open it to see the next safe step.', status: 'Try again' },
};

/**
 * Tonight: one open grouped list of the renders waiting on this listener and
 * voice. Each row is a single focus stop that opens the review. Polling,
 * status derivation and the exact-pair filter are unchanged from the
 * workbench it replaces.
 */
const RendersList: React.FC = () => {
  const { state, fetchStories } = useApp();
  const context = useBedtimeContext();
  const [renders, setRenders] = useState<RenderListItem[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'success' | 'empty' | 'error'>('loading');
  const [error, setError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [refreshError, setRefreshError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshInFlightRef = useRef(false);
  const pollControllerRef = useRef<AbortController | null>(null);
  const loadRequestRef = useRef(0);
  const pair = context.pair;
  const currentPairKey = pair ? `${pair.householdId}:${pair.listenerId}:${pair.voiceId}` : '';
  const pairKeyRef = useRef(currentPairKey);
  pairKeyRef.current = currentPairKey;
  const [loadedPairKey, setLoadedPairKey] = useState<string | null>(null);

  useEffect(() => { void fetchStories(); }, [fetchStories]);

  const load = useCallback(async (signal?: AbortSignal, background = false) => {
    if (!pair) return;
    const requestPairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    const requestId = ++loadRequestRef.current;
    const isCurrent = () => requestId === loadRequestRef.current && requestPairKey === pairKeyRef.current;
    if (background) setRefreshing(true);
    if (!background) {
      setLoadState('loading');
      setError(null);
      setLoadedPairKey(null);
    }
    try {
      const result = await fetchRenders({ householdId: pair.householdId, listenerId: pair.listenerId, voiceId: pair.voiceId, signal });
      if (!isCurrent()) return;
      const exact = result.filter((render) => render.household_id === pair.householdId && render.listener_id === pair.listenerId && render.voice_id === pair.voiceId && render.status !== 'parent_approved');
      setRenders(exact);
      if (!background) setLoadedPairKey(requestPairKey);
      setRefreshError(null);
      setLoadState(exact.length ? 'success' : 'empty');
    } catch (requestError) {
      if (!isAbortError(requestError) && isCurrent()) {
        const safeError = toApiError(requestError);
        if (background) setRefreshError(safeError);
        else { setError(safeError); setLoadedPairKey(requestPairKey); setLoadState('error'); }
      }
    } finally {
      if (background && isCurrent()) setRefreshing(false);
    }
  }, [pair]);

  useEffect(() => {
    if (!pair || context.status === 'loading' || context.status === 'needs_selection' || context.status === 'error') return undefined;
    const controller = new AbortController();
    setRenders([]);
    setLoadedPairKey(null);
    setRefreshError(null);
    void load(controller.signal);
    return () => {
      controller.abort();
      loadRequestRef.current += 1;
    };
  }, [context.status, load, pair]);

  const anyInFlight = renders.some((render) => ['queued', 'rendering', 'regenerating'].includes(render.status));
  useEffect(() => {
    if (!anyInFlight || !pair) return undefined;
    let timer: number | undefined;
    const poll = () => {
      if (document.visibilityState !== 'visible' || refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      const controller = new AbortController();
      pollControllerRef.current = controller;
      void load(controller.signal, true).finally(() => {
        if (pollControllerRef.current === controller) pollControllerRef.current = null;
        refreshInFlightRef.current = false;
      });
    };
    timer = window.setInterval(poll, 8000);
    const onVisibility = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      pollControllerRef.current?.abort();
      pollControllerRef.current = null;
      refreshInFlightRef.current = false;
    };
  }, [anyInFlight, load, pair]);

  const groups = useMemo(() => groupTonightRenders(renders), [renders]);
  const storyFor = (storyId: string) => state.stories.find((story) => story.story_id === storyId);
  const titleFor = (storyId: string) => storyFor(storyId)?.title || storyId;

  if (context.status === 'needs_selection') return <div className="tonight"><EmptyState headingLevel={1} title="Choose bedtime" message="Pick who is listening and the Family voice they will hear." action={{ label: 'Choose bedtime', onClick: () => context.openPairDialog() }} testId="tonight-blocked" /></div>;
  if (context.status === 'no_listener') return <div className="tonight"><EmptyState headingLevel={1} title="Add a listener first" message="Luma uses a listener to keep stories and goodnights personal." action={{ label: 'Add a listener', to: '/account' }} testId="tonight-blocked" /></div>;
  if (context.status === 'no_ready_voice') return <div className="tonight"><EmptyState headingLevel={1} title="Add a family voice" message="Add a family voice before making a story." action={{ label: 'Add a family voice', to: '/voice' }} testId="tonight-blocked" /></div>;
  if (!pair && context.status === 'missing_bookends') {
    return <div className="tonight"><EmptyState headingLevel={1} title="Add a hello and goodnight" message="Add the hello and goodnight before making a new story." action={{ label: 'Record personal messages', to: '/voice' }} testId="tonight-blocked" /></div>;
  }
  if (pair && context.status === 'missing_bookends') {
    const listenerName = context.listeners.find((listener) => listener.listener_id === pair.listenerId)?.display_name || 'this listener';
    return <div className="tonight"><EmptyState headingLevel={1} title={`Add ${listenerName}’s hello and goodnight`} message="Record three personal messages before making a new story." action={{ label: 'Record personal messages', to: `/voice/${pair.voiceId}/personalize/${pair.listenerId}` }} testId="tonight-blocked" /></div>;
  }
  if (!pair || context.status === 'loading') return <div className="tonight"><StateMessage kind="loading" title="Loading your bedtime choices…" headingLevel={1} testId="tonight-context-loading" /></div>;
  if (context.status === 'error') return <div className="tonight"><StateMessage kind="error" error={context.error as any} headingLevel={1} retry={{ label: 'Try again', onClick: () => void context.refresh() }} testId="tonight-context-error" /></div>;

  const pairLoaded = loadedPairKey === currentPairKey;
  const listenerName = context.listeners.find((listener) => listener.listener_id === pair.listenerId)?.display_name || 'your listener';
  return (
    <div className="tonight" data-testid="render-list" aria-busy={refreshing}>
      <header className="tonight__head">
        <h1>Stories for {listenerName}</h1>
        <p className="tonight__subtitle">Listen before bedtime.</p>
      </header>
      {!pairLoaded && <StateMessage kind="loading" title="Checking recordings…" testId="tonight-loading" />}
      {pairLoaded && loadState === 'error' && <StateMessage kind="error" error={error} retry={{ label: 'Try again', onClick: () => void load() }} testId="tonight-error" />}
      {pairLoaded && loadState === 'empty' && <EmptyState headingLevel={2} title="Ready for another story?" message="Choose a story in Discover for your listener." action={{ label: 'Discover stories', to: '/discover' }} testId="tonight-empty" />}
      {pairLoaded && refreshError && renders.length > 0 && (
        <StateMessage
          kind="error"
          error={refreshError}
          title="Tonight could not refresh"
          retry={{ label: 'Try again', busyLabel: 'Trying again…', busy: refreshing, onClick: () => void load(undefined, true) }}
          testId="tonight-refresh-error"
        />
      )}
      {pairLoaded && loadState === 'success' && (Object.keys(groups) as TonightGroup[]).map((group) => groups[group].length ? (
        <section key={group} className="tonight-group" aria-labelledby={`tonight-${group}`} data-testid={`tonight-group-${group}`}>
          <h2 id={`tonight-${group}`}>{GROUP_COPY[group].title}</h2>
          <p className="tonight-group__support">{GROUP_COPY[group].description}</p>
          <ul className="tonight-list">
            {groups[group].map((render) => {
              const story = storyFor(render.story_id);
              return (
                <li key={render.render_id} className="tonight-list__item">
                  <Link to={`/renders/${render.render_id}`} className="tonight-row">
                    <span className="tonight-row__art">
                      <StoryArtwork src={story?.thumbnail_url} srcSet={story?.thumbnail_srcset} sizes="64px" imageClassName="tonight-row__image" fallbackClassName="tonight-row__fallback" fallbackSize={22} />
                    </span>
                    <span className="tonight-row__text">
                      <span className="tonight-row__title">{titleFor(render.story_id)}</span>
                      <span className="tonight-row__meta">
                        <span>for {render.listener_name || 'your listener'}</span>
                        <span>{formatDurationLabel(render.estimated_duration_minutes, render.output_seconds) || 'Preparing story'}</span>
                      </span>
                    </span>
                    <span className={`tonight-row__status tonight-row__status--${group}`}>{GROUP_COPY[group].status}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null)}
      <p className="tonight__shelf-link">Find approved stories in <Link to="/my-stories">My stories</Link>.</p>
    </div>
  );
};

export default RendersList;
